// src/client/renderer/emote-bubble-renderer.ts
//
// Plan 24b Phase B, Task 2.1 — Emote Speech Bubble THREE.Sprite renderer.
//
// Subscribes to `emote_played` ServerMessages, looks up the player's character
// rig (per Plan 13's `CharacterAnchors.speechBubble: THREE.Vector3` at
// preview-rig.ts:48-49), constructs a `THREE.Sprite` with the matching emote
// icon texture, and adds it to `rig.root` at the speech-bubble anchor. The
// sprite has a 2-second lifetime: 200ms scale-in (ease-in), 1600ms hold,
// 200ms scale-out (ease-out). On completion the sprite is removed and its
// material is disposed (the texture is owned by Plan 9's loader).
//
// THREE.js API verified via Context7 MCP (resolve-library-id "three" →
// query-docs "Sprite SpriteMaterial constructor map dispose"):
//
//   - `new THREE.SpriteMaterial({ map: texture, transparent: true })` —
//     constructor takes a parameters object; `map` is the diffuse texture and
//     `transparent: true` enables alpha blending.
//     Source: https://github.com/mrdoob/three.js/blob/dev/docs/pages/SpriteMaterial.html.md
//   - `new THREE.Sprite(material)` — single positional `SpriteMaterial` arg.
//     Source: same doc page.
//   - `material.dispose()` — releases the GPU material program. Per the
//     "How to dispose of Objects > Textures" section: "Disposing a material
//     does not affect textures, which are handled separately since a single
//     texture can be used by multiple materials." So calling
//     `material.dispose()` here does NOT touch `texture` — Plan 9's asset
//     loader owns the texture lifetime.
//   - `sprite.position.copy(vec3)` — Vector3.copy is the standard mutate-in-place
//     copy used throughout three.js demos (see manual/billboards.html for the
//     `sprite.position.set(x, y, z)` parallel; we use copy() because the input
//     is already a Vector3).
//
// GSAP API used:
//   - `tl.fromTo(target, fromVars, toVars, position)` — animates target from
//     `fromVars` to `toVars` starting at `position` seconds.
//   - `tl.to(target, vars, position)` — tweens target to `vars`, starting at
//     `position` seconds.
//   - `tl.call(callback, params, position)` — invokes `callback` at absolute
//     `position` seconds; per GSAP docs the callback "stays in its defined
//     place" even if more tweens are appended after.
//
// Multi-player lifecycle:
//   - Internal `Map<playerId, BubbleState>` tracks active bubbles. On a new
//     `emote_played` for a player that already has a bubble, the prior bubble
//     is cleaned up FIRST (sprite removed, material disposed, timeline killed)
//     before the new one is constructed. This implements the spec contract:
//     "replaces any existing bubble on the same player".

import * as THREE from 'three';
import type { GsapRegistry, GsapTimelineHandle } from './vfx/gsap-registry';
import type { ServerMessage } from '@shared/protocol';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** Plan 13's character rig surface visible to the renderer. */
export interface RigForEmote {
  /** Scene-graph root — renderer adds the sprite as a child of this. */
  root: THREE.Object3D;
  /** Rig-local speech bubble anchor (from preview-rig.ts:48-49). */
  anchors: { speechBubble: THREE.Vector3 };
}

/** Lookup callback the orchestrator implements (Plan 19 / Plan 24c). Returns null if rig not loaded. */
export type RigGetter = (playerId: string) => RigForEmote | null;

/** Emote-icon texture lookup callback. Plan 9 wires real textures; tests pass fakes. */
export type EmoteTextureGetter = (emoteId: 1 | 2 | 3 | 4 | 5 | 6) => THREE.Texture | null;

export interface EmoteBubbleRendererDeps {
  gsapRegistry: GsapRegistry;
  /** Subscribe to ServerMessages — same DI pattern as BoardMountPayload.subscribe. */
  subscribe(handler: (msg: ServerMessage) => void): () => void;
  /** Look up the rig for a given player id. Returns null if rig not loaded yet. */
  getPlayerRig: RigGetter;
  /** Look up the emote-icon texture by emoteId. Returns null if texture missing. */
  getEmoteTexture: EmoteTextureGetter;
}

export interface EmoteBubbleRendererHandle {
  /** Clears the active bubble for one player, if present. */
  clearPlayer(playerId: string): void;
  /** Idempotent. Disposes all in-flight bubbles + unsubscribes. */
  dispose(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation timing (spec roadmap line 421: 2s lifetime with ease-in-out scale)
// ─────────────────────────────────────────────────────────────────────────────

const SCALE_IN_DURATION = 0.2; // 200ms scale 0 → 1
const HOLD_DURATION = 1.6; // 1600ms hold at scale 1
const SCALE_OUT_DURATION = 0.2; // 200ms scale 1 → 0
const TOTAL_LIFETIME = SCALE_IN_DURATION + HOLD_DURATION + SCALE_OUT_DURATION; // 2.0s

const SCALE_OUT_START = SCALE_IN_DURATION + HOLD_DURATION; // 1.8s
const EMOTE_BUBBLE_TARGET_SCALE = 5;
const EMOTE_BUBBLE_CLEARANCE_GAP = 0.35;
const EMOTE_BUBBLE_CENTER_Y_OFFSET = EMOTE_BUBBLE_TARGET_SCALE / 2 + EMOTE_BUBBLE_CLEARANCE_GAP;
const EMOTE_BUBBLE_RETRY_INTERVAL_MS = 50;
const EMOTE_BUBBLE_RETRY_WINDOW_MS = 500;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

interface BubbleState {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  timelineHandle: GsapTimelineHandle;
  /** Captured at construction so cleanup removes from the SAME parent even
   *  if the caller's `getPlayerRig` later returns a different rig. */
  rig: RigForEmote;
}

interface PendingBubbleState {
  emoteId: 1 | 2 | 3 | 4 | 5 | 6;
  startedAt: number;
  timeoutId: ReturnType<typeof setTimeout> | null;
  lastMissing: 'rig' | 'texture';
}

type BubbleRenderResult = 'rendered' | 'missing_rig' | 'missing_texture';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createEmoteBubbleRenderer(
  deps: EmoteBubbleRendererDeps,
): EmoteBubbleRendererHandle {
  const { gsapRegistry, subscribe, getPlayerRig, getEmoteTexture } = deps;

  /** playerId → active bubble. Replacing an existing entry is the "replaces
   *  any existing bubble on the same player" spec contract (roadmap line 421). */
  const activeBubbles = new Map<string, BubbleState>();
  const pendingBubbles = new Map<string, PendingBubbleState>();

  let disposed = false;

  // ── Cleanup helper (used both by timeline-completion and by replacement) ──
  function cleanupBubble(playerId: string): void {
    const entry = activeBubbles.get(playerId);
    if (entry === undefined) return;

    // Remove from scene graph. We use the rig CAPTURED at construction time
    // (entry.rig) rather than calling getPlayerRig(playerId) again, so cleanup
    // is robust to mid-bubble rig swaps (e.g. character re-customization).
    entry.rig.root.remove(entry.sprite);

    // Dispose the material — does NOT dispose the texture (texture lifetime is
    // owned by Plan 9's asset loader; multiple bubbles may share a texture).
    entry.material.dispose();

    // Kill the GSAP timeline (releases the registered tween + sentinel).
    entry.timelineHandle.dispose();

    activeBubbles.delete(playerId);
  }

  function clearPendingBubble(playerId: string): void {
    const pending = pendingBubbles.get(playerId);
    if (pending == null) return;
    if (pending.timeoutId != null) clearTimeout(pending.timeoutId);
    pendingBubbles.delete(playerId);
  }

  // ── Per-emote handler ─────────────────────────────────────────────────────
  function renderEmotePlayed(playerId: string, emoteId: 1 | 2 | 3 | 4 | 5 | 6): BubbleRenderResult {
    if (disposed) return 'missing_rig';

    const rig = getPlayerRig(playerId);
    if (rig === null) {
      return 'missing_rig';
    }

    const texture = getEmoteTexture(emoteId);
    if (texture === null) {
      return 'missing_texture';
    }

    // Replace any existing bubble for this player BEFORE creating the new one.
    if (activeBubbles.has(playerId)) {
      cleanupBubble(playerId);
    }

    // Build the sprite. Note: SpriteMaterial defaults to `transparent: true`
    // per the docs response example, but we set it explicitly to make the
    // intent clear and to defend against future three.js default changes.
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.renderOrder = 1200;

    // Position in rig-local space — the rig.root's world transform will carry
    // the sprite into world space when the scene graph updates each frame.
    sprite.position.copy(rig.anchors.speechBubble);
    sprite.position.y += EMOTE_BUBBLE_CENTER_Y_OFFSET;

    // Start invisible; the timeline scales in immediately.
    sprite.scale.set(0, 0, 0);

    rig.root.add(sprite);

    // Build the 2-second timeline. Per the canonical pattern in
    // turn-banner.ts:209-230, every fromTo/to/call uses an explicit numeric
    // position (anti-pattern guardrail F20a-CLASS-1).
    const timelineHandle = gsapRegistry.create(`emote-bubble:${playerId}:${emoteId}`);
    const tl = timelineHandle.timeline;

    // Phase 1 (0s → 0.2s): scale 0 → 1, ease-in.
    tl.fromTo(
      sprite.scale,
      { x: 0, y: 0, z: 0 },
      {
        x: EMOTE_BUBBLE_TARGET_SCALE,
        y: EMOTE_BUBBLE_TARGET_SCALE,
        z: EMOTE_BUBBLE_TARGET_SCALE,
        duration: SCALE_IN_DURATION,
        ease: 'power2.in',
      },
      0,
    );

    // Phase 2 (0.2s → 1.8s): hold at scale 1 — implicit, no tween needed.

    // Phase 3 (1.8s → 2.0s): scale 1 → 0, ease-out.
    tl.to(
      sprite.scale,
      { x: 0, y: 0, z: 0, duration: SCALE_OUT_DURATION, ease: 'power2.out' },
      SCALE_OUT_START,
    );

    // Cleanup at t = 2.0s. Captured `playerId` is the cleanup key so a
    // replaced-bubble's lingering callback (if it somehow fires after
    // cleanupBubble already ran) becomes a no-op via the `entry === undefined`
    // guard inside cleanupBubble().
    tl.call(
      () => {
        cleanupBubble(playerId);
      },
      undefined,
      TOTAL_LIFETIME,
    );

    activeBubbles.set(playerId, { sprite, material, timelineHandle, rig });
    return 'rendered';
  }

  function warnSkippedBubble(
    playerId: string,
    emoteId: 1 | 2 | 3 | 4 | 5 | 6,
    missing: 'rig' | 'texture',
  ): void {
    if (missing === 'rig') {
      console.warn(
        `[EmoteBubbleRenderer] getPlayerRig("${playerId}") returned null — rig not loaded after retry; skipping emote ${emoteId}.`,
      );
      return;
    }
    console.warn(
      `[EmoteBubbleRenderer] getEmoteTexture(${emoteId}) returned null — emote texture missing after retry; skipping for player "${playerId}".`,
    );
  }

  function handleEmotePlayed(playerId: string, emoteId: 1 | 2 | 3 | 4 | 5 | 6): void {
    if (disposed) return;
    clearPendingBubble(playerId);

    const startedAt = Date.now();
    const attemptRender = (): void => {
      if (disposed) return;
      const result = renderEmotePlayed(playerId, emoteId);
      if (result === 'rendered') {
        pendingBubbles.delete(playerId);
        return;
      }

      const missing = result === 'missing_rig' ? 'rig' : 'texture';
      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs >= EMOTE_BUBBLE_RETRY_WINDOW_MS) {
        pendingBubbles.delete(playerId);
        warnSkippedBubble(playerId, emoteId, missing);
        return;
      }

      const timeoutId = setTimeout(attemptRender, EMOTE_BUBBLE_RETRY_INTERVAL_MS);
      pendingBubbles.set(playerId, { emoteId, startedAt, timeoutId, lastMissing: missing });
    };

    pendingBubbles.set(playerId, { emoteId, startedAt, timeoutId: null, lastMissing: 'rig' });
    attemptRender();
  }

  // ── ServerMessage subscriber ──────────────────────────────────────────────
  function handleMessage(msg: ServerMessage): void {
    if (disposed) return;
    // emote_played is a TOP-LEVEL ServerMessage variant per
    // src/shared/protocol/messages.ts:59 (NOT nested under `event`).
    if (msg.type !== 'emote_played') return;
    handleEmotePlayed(msg.playerId, msg.emoteId);
  }

  const unsubscribe = subscribe(handleMessage);

  // ── dispose() ──────────────────────────────────────────────────────────────
  function dispose(): void {
    if (disposed) return;
    disposed = true;

    unsubscribe();
    for (const playerId of Array.from(pendingBubbles.keys())) {
      clearPendingBubble(playerId);
    }

    // Snapshot the keys: cleanupBubble mutates the map.
    for (const playerId of Array.from(activeBubbles.keys())) {
      cleanupBubble(playerId);
    }
    // Map is empty by here (cleanupBubble deletes each entry); .clear() is a
    // belt-and-braces reset against any future code path that could leave a
    // dangling entry.
    activeBubbles.clear();
  }

  return {
    clearPlayer(playerId: string): void {
      clearPendingBubble(playerId);
      cleanupBubble(playerId);
    },
    dispose,
  };
}
